"use client";

import { useState, useEffect, useCallback } from "react";
import Image from "next/image";
import { Link } from "@/navigation";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";

/** 单个案例配置：标题/描述用翻译 key，图片路径必传 */
export interface CaseItemConfig {
	titleKey: string;
	descKey: string;
	image: string;
}

// 引号图标组件
const QuoteIcon = () => (
	<svg
		className="h-10 w-10 text-themePrimary400 sm:h-12 sm:w-12 md:h-14 md:w-14"
		fill="currentColor"
		viewBox="0 0 24 24"
		aria-hidden="true"
	>
		<path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
	</svg>
);

// 45度倾斜箭头图标
const ArrowIcon = () => (
	<svg
		className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
		viewBox="0 0 16 16"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		<path d="M4 12L12 4M12 4H6M12 4V10" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);

const DEFAULT_CASES: CaseItemConfig[] = [
	{
		titleKey: "case1Title",
		descKey: "case1Desc",
		image:
			"/image/new-version/machining/five-axis/case/case-study-js-precision-enables-advanced-turbine-blade-manufacturing.webp",
	},
	{
		titleKey: "case2Title",
		descKey: "case2Desc",
		image:
			"/image/new-version/machining/five-axis/case/case-study-js-precision-powers-advanced-aerospace-propulsion-systems.webp",
	},
	{
		titleKey: "case3Title",
		descKey: "case3Desc",
		image:
			"/image/new-version/machining/five-axis/case/dr-park-collaborates-with-js-precision-to-advance-orthopedic-rehabilitation-technology.webp",
	},
];

export interface CaseProps {
	/** 翻译命名空间，需包含 title1/title2 或 sectionTitle，以及各 case 的 titleKey/descKey、readCaseStudy */
	translationNamespace?: string;
	/** 案例列表，每项可传入独立图片；不传则用默认 3 条（五轴页） */
	cases?: CaseItemConfig[];
	/** Read Case Study 链接，以 http 开头则用外链并新窗口打开 */
	readCaseStudyHref?: string;
	/** 可选：使用单行标题时传入此 key（如 sectionTitle），不传则使用 title1 + title2 */
	sectionTitleKey?: string;
	className?: string;
}

export default function Case({
	translationNamespace = "fiveAxisCase",
	cases = DEFAULT_CASES,
	readCaseStudyHref = "/cases",
	sectionTitleKey,
	className,
}: CaseProps) {
	const t = useTranslations(translationNamespace);
	const [currentIndex, setCurrentIndex] = useState(0);

	const nextSlide = useCallback(() => {
		setCurrentIndex((prev) => (prev + 1) % cases.length);
	}, [cases.length]);

	useEffect(() => {
		const timer = setInterval(nextSlide, 5000);
		return () => clearInterval(timer);
	}, [nextSlide]);

	const currentCase = cases[currentIndex];
	const isExternalLink = readCaseStudyHref.startsWith("http");

	return (
		<section className={cn("w-full bg-white py-8 sm:py-10 md:py-12 lg:py-16", className)}>
			<div className="container">
				{/* 标题区域 - 居中 */}
				<div className="mb-6 text-center sm:mb-8 md:mb-10 lg:mb-12">
					<h2 className="text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">
						{sectionTitleKey ? (
							t(sectionTitleKey)
						) : (
							<>
								{t("title1")}
								<br />
								{t("title2")}
							</>
						)}
					</h2>
				</div>

				{/* 案例内容区 */}
				<div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:gap-12 xl:gap-16">
					{/* 左侧文字内容 */}
					<div className="w-full lg:w-1/2">
						{/* 案例标题 - 斜体 */}
						<h3 className="mb-6 text-lg font-bold italic !leading-snug text-black sm:text-xl md:text-2xl">
							{t(currentCase.titleKey)}
						</h3>

						{/* 引号图标 */}
						<div className="mb-4">
							<QuoteIcon />
						</div>

						{/* 案例描述 */}
						<p className="mb-6 text-sm leading-relaxed text-black md:text-base">{t(currentCase.descKey)}</p>

						{/* Read Case Study 链接 */}
						{isExternalLink ? (
							<a
								href={readCaseStudyHref}
								target="_blank"
								rel="noopener noreferrer"
								className="group inline-flex items-center gap-2 text-sm font-medium text-themePrimary400 transition-colors hover:text-mainColor md:text-base"
							>
								{t("readCaseStudy")}
								<ArrowIcon />
							</a>
						) : (
							<Link
								href={readCaseStudyHref}
								className="group inline-flex items-center gap-2 text-sm font-medium text-themePrimary400 transition-colors hover:text-mainColor md:text-base"
							>
								{t("readCaseStudy")}
								<ArrowIcon />
							</Link>
						)}
					</div>

					{/* 右侧图片 */}
					<div className="w-full lg:w-1/2">
						<div className="relative aspect-[4/3] w-full overflow-hidden">
							<Image
								src={currentCase.image}
								alt={t(currentCase.titleKey)}
								fill
								className="object-cover transition-opacity duration-500"
								sizes="(max-width: 1024px) 100vw, 50vw"
							/>
						</div>

						{/* 分页圆点 */}
						<div className="mt-6 flex items-center justify-center gap-2">
							{cases.map((_, index) => (
								<button
									key={index}
									onClick={() => setCurrentIndex(index)}
									className={`h-2.5 w-2.5 rounded-full transition-colors duration-300 ${
										currentIndex === index ? "bg-themePrimary400" : "bg-gray-300"
									}`}
									aria-label={`Go to case ${index + 1}`}
								/>
							))}
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
